import { gql } from '@apollo/client';

/**
 * Cash Flow report (ТЗ 0023).
 *
 * The statement is built on the server from bank transactions alone, and the
 * response shape is deliberate: `rows` is flat with `level`/`parentKey` so
 * expanding a group is a filter over an array we already hold, and `values` is
 * positional against `periods` so no per-cell lookup is needed for a 45×33
 * matrix. Total rows are absent on purpose — they are drawn from `periods`,
 * which is what keeps "sum of rows" from drifting away from "the total".
 */
export const CASH_FLOW_REPORT = gql`
  query CashFlowReport(
    $dateFrom: Date
    $dateTo: Date
    $grain: String
    $includeInternal: Boolean
  ) {
    cashFlowReport(
      dateFrom: $dateFrom
      dateTo: $dateTo
      grain: $grain
      includeInternal: $includeInternal
    ) {
      grain
      currency
      openingCash
      closingCash
      asOf
      lastPeriodComplete
      totalRevenue
      totalReturns
      totalOutflow
      totalOperatingCashFlow
      totalFunding
      rateMissingCount
      internalExcludedCount
      monthlyBurnPln
      runwayMonths
      accrual {
        invoiced
        received
        difference
        invoiceCount
      }
      periods {
        key
        revenue
        returns
        outflow
        operatingCashFlow
        funding
        net
        cash
        rows
      }
      rows {
        key
        label
        level
        parentKey
        section
        group
        hasChildren
        values
        total
      }
    }
  }
`;

export const CASH_FLOW_TRANSACTIONS = gql`
  query CashFlowTransactions(
    $dateFrom: Date
    $dateTo: Date
    $categorySlugs: [String]
    $direction: String
    $uncategorisedOnly: Boolean
    $needsReview: Boolean
    $search: String
    $page: Int
    $perPage: Int
  ) {
    bankTransactions(
      dateFrom: $dateFrom
      dateTo: $dateTo
      categorySlugs: $categorySlugs
      direction: $direction
      uncategorisedOnly: $uncategorisedOnly
      needsReview: $needsReview
      search: $search
      page: $page
      perPage: $perPage
    ) {
      totalCount
      page
      perPage
      summary {
        count
        inflowPln
        outflowPln
      }
      items {
        pk
        bookedAt
        amount
        currency
        balanceAfter
        accountName
        rawType
        description
        counterpartyName
        categorySlug
        categoryName
        categorySource
        isInternalTransfer
        needsReview
        links {
          pk
          targetType
          targetId
          targetLabel
          matchSource
        }
      }
    }
  }
`;

export const CASH_FLOW_DICTIONARIES = gql`
  query CashFlowDictionaries {
    cashFlowCategories {
      pk
      slug
      name
      group
      section
      learningPolicy
    }
    bankAccounts {
      pk
      slug
      name
      kind
      transactionsCount
      netAmountPln
    }
  }
`;

export const CASH_FLOW_CONTROL = gql`
  query CashFlowControl {
    cashFlowControl {
      transactionsCount
      uncategorisedCount
      internalCount
      internalUnpairedCount
      needsReviewCount
      heuristicDedupCount
      rulesTotal
      rulesNeedingAttention
      accounts {
        pk
        slug
        name
        iban
        transactionsCount
        netAmountPln
      }
      checks {
        code
        status
        detail
        subject
        count
      }
    }
  }
`;

export const CASH_FLOW_RULES = gql`
  query CashFlowRules(
    $isActive: Boolean
    $health: String
    $needsAttention: Boolean
    $page: Int
    $perPage: Int
  ) {
    cashFlowRules(
      isActive: $isActive
      health: $health
      needsAttention: $needsAttention
      page: $page
      perPage: $perPage
    ) {
      pk
      keyKind
      keyValue
      direction
      categorySlug
      categoryName
      priority
      isActive
      health
      origin
      hitsCount
      overridesCount
      validFrom
      validTo
    }
  }
`;

/** Import history — previews included; seeing what a file WOULD do is the point. */
export const BANK_STATEMENT_IMPORTS = gql`
  query BankStatementImports($limit: Int) {
    bankStatementImports(limit: $limit) {
      pk
      fileName
      status
      dryRun
      rowsTotal
      createdCount
      duplicateCount
      failedCount
      balanceGapCount
      periodFrom
      periodTo
      errorMessage
      createdAt
      createdByName
    }
  }
`;

export const CASH_FLOW_SETTINGS = gql`
  query CashFlowSettings {
    cashFlowSettings {
      pk
      ownPartyNames
      internalTransferAutodetect
      internalTransferWindowDays
    }
  }
`;

export const GATEWAY_SETTLEMENTS = gql`
  query GatewaySettlements($status: String) {
    gatewaySettlements(status: $status) {
      pk
      provider
      merchantCode
      externalRef
      bookedAt
      amountBank
      itemsTotal
      feeAmount
      feeSource
      unallocated
      status
      reasonCode
      items {
        pk
        invoiceId
        invoiceNumber
        kind
        amount
        matchSource
      }
    }
  }
`;

export const GATEWAY_SETTLEMENT_CANDIDATES = gql`
  query GatewaySettlementCandidates($settlementId: ID!) {
    gatewaySettlementCandidates(settlementId: $settlementId) {
      invoiceId
      number
      issueDate
      amount
      preselected
      alreadyInSettlementId
    }
  }
`;

/**
 * «Где эти деньги в банке» for one document.
 *
 * A root query rather than a field on `FakturowniaInvoiceType` and friends:
 * those types are shared by both frontends and several features, and widening
 * them here would churn generated hooks for consumers that never asked.
 */
export const DOCUMENT_CASH_FLOW = gql`
  query DocumentCashFlow($targetType: String!, $targetId: ID!) {
    documentCashFlow(targetType: $targetType, targetId: $targetId) {
      status
      totalPln
      settlementProvider
      settlementRef
      settlementBookedAt
      rows {
        pk
        bookedAt
        amount
        currency
        accountName
        description
        counterpartyName
        matchSource
      }
    }
  }
`;

/**
 * Documents this row might be explaining.
 *
 * Offered, never applied: the matcher links only when the answer is
 * unambiguous, and most of the expense side is card payments with no document
 * at all — that is where a person has to decide.
 */
export const BANK_TRANSACTION_MATCH_CANDIDATES = gql`
  query BankTransactionMatchCandidates($transactionId: ID!) {
    bankTransactionMatchCandidates(transactionId: $transactionId) {
      targetType
      targetId
      label
      amount
      confidence
      strategy
    }
  }
`;
